/**
 * Favorites/Bookmarks context for The HospiceGuard
 * Now syncs with backend API for persistence
 */
import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API, useAuth } from '../App';

const FavoritesContext = createContext(null);

export const useFavorites = () => {
  const context = useContext(FavoritesContext);
  if (!context) {
    throw new Error('useFavorites must be used within FavoritesProvider');
  }
  return context;
};

export const FavoritesProvider = ({ children }) => {
  const { token, user } = useAuth();
  const [favorites, setFavorites] = useState([]);
  const [recentPatients, setRecentPatients] = useState([]);
  const [loading, setLoading] = useState(true);

  // Load favorites from backend API
  useEffect(() => {
    if (!user || !token) {
      setFavorites([]);
      setRecentPatients([]);
      return;
    }

    const loadData = async () => {
      try {
        // Load favorites from API
        const favResponse = await axios.get(`${API}/favorites`);
        if (favResponse.data?.favorites) {
          setFavorites(favResponse.data.favorites.map(f => ({
            id: f.audit_id,
            patient_name: f.patient_name,
            lcd_type: f.lcd_type,
            score: f.score,
            favorited_at: f.favorited_at
          })));
        }
        
        // Load recent patients from API
        const recentResponse = await axios.get(`${API}/patients/recent?limit=20`);
        if (recentResponse.data?.recent_patients) {
          setRecentPatients(recentResponse.data.recent_patients.map(r => ({
            id: r.last_audit_id,
            patient_name: r.patient_name,
            lcd_type: r.lcd_type,
            discipline: r.discipline,
            accessed_at: r.last_accessed
          })));
        }
      } catch (error) {
        console.error('Failed to load favorites/recent from API:', error);
        // Fallback to localStorage
        const storedFavorites = localStorage.getItem(`favorites_${user.id}`);
        const storedRecent = localStorage.getItem(`recent_${user.id}`);
        
        if (storedFavorites) {
          try {
            setFavorites(JSON.parse(storedFavorites));
          } catch (e) {
            console.error('Failed to parse stored favorites');
          }
        }
        
        if (storedRecent) {
          try {
            setRecentPatients(JSON.parse(storedRecent));
          } catch (e) {
            console.error('Failed to parse stored recent');
          }
        }
      }
      
      setLoading(false);
    };

    loadData();
  }, [user, token]);

  // Backup to localStorage whenever favorites change
  useEffect(() => {
    if (user && favorites.length >= 0) {
      localStorage.setItem(`favorites_${user.id}`, JSON.stringify(favorites));
    }
  }, [favorites, user]);

  useEffect(() => {
    if (user && recentPatients.length >= 0) {
      localStorage.setItem(`recent_${user.id}`, JSON.stringify(recentPatients));
    }
  }, [recentPatients, user]);

  const addFavorite = useCallback(async (audit) => {
    // Check if already favorited
    if (favorites.some(f => f.id === audit.id)) {
      return;
    }
    
    // Optimistic update
    const newFavorite = {
      id: audit.id,
      patient_name: audit.patient_name,
      lcd_type: audit.lcd_type,
      score: audit.rewritten_scorecard?.overall_score || audit.original_scorecard?.overall_score,
      favorited_at: new Date().toISOString()
    };
    
    setFavorites(prev => [newFavorite, ...prev].slice(0, 50));
    
    // Sync to backend
    try {
      await axios.post(`${API}/favorites`, {
        audit_id: audit.id,
        patient_name: audit.patient_name,
        lcd_type: audit.lcd_type,
        score: newFavorite.score
      });
    } catch (error) {
      console.error('Failed to save favorite to backend:', error);
    }
  }, [favorites, token]);

  const removeFavorite = useCallback(async (auditId) => {
    // Optimistic update
    setFavorites(prev => prev.filter(f => f.id !== auditId));
    
    // Sync to backend
    try {
      await axios.delete(`${API}/favorites/${auditId}`);
    } catch (error) {
      console.error('Failed to remove favorite from backend:', error);
    }
  }, [token]);

  const isFavorite = useCallback((auditId) => {
    return favorites.some(f => f.id === auditId);
  }, [favorites]);

  const toggleFavorite = useCallback(async (audit) => {
    if (isFavorite(audit.id)) {
      await removeFavorite(audit.id);
      return false;
    } else {
      await addFavorite(audit);
      return true;
    }
  }, [isFavorite, removeFavorite, addFavorite]);

  const addRecentPatient = useCallback((audit) => {
    setRecentPatients(prev => {
      // Remove if already exists (will re-add at top)
      const filtered = prev.filter(r => r.id !== audit.id);
      
      const newRecent = {
        id: audit.id,
        patient_name: audit.patient_name,
        lcd_type: audit.lcd_type,
        discipline: audit.discipline,
        accessed_at: new Date().toISOString()
      };
      
      return [newRecent, ...filtered].slice(0, 20); // Keep max 20 recent
    });
  }, []);

  const clearRecentPatients = useCallback(() => {
    setRecentPatients([]);
  }, []);

  return (
    <FavoritesContext.Provider value={{
      favorites,
      recentPatients,
      loading,
      addFavorite,
      removeFavorite,
      isFavorite,
      toggleFavorite,
      addRecentPatient,
      clearRecentPatients
    }}>
      {children}
    </FavoritesContext.Provider>
  );
};

export default FavoritesProvider;
